import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY, from } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AppState } from '../reducers';
import { loadNewOrderSelect, loadNewOrderSelectFailure, loadNewOrderSelectSuccess } from './new-order-select.actions';
import { NewOrderSelectApiService } from './new-order-select.api.service';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbOtCatalogName, IdbOtCatalogTypeMap } from 'src/app/idb/idb-catalog-name.enum';

/** Map of option values eligible for IDB caching (only parameterless GETs) */
const IDB_CATALOG_BY_OPTION: Record<string, IdbOtCatalogName> = {
  DeliveryMethod: IdbOtCatalogName.DeliveryMethods,
  'Receiver/GetCountriesReceivers': IdbOtCatalogName.CountriesReceivers,
  OrderStatus: IdbOtCatalogName.OrderStatuses,
};

@Injectable()
export class NewOrderSelectEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private api: NewOrderSelectApiService,
    private idb: ManageIdbService,
  ) {}

  // Dedupe concurrent requests per key
  private pending: Record<string, boolean> = {};

  private _toErrorMessage(error: unknown): string {
    if (!error) {
      return 'Unknown error';
    }

    if (typeof error === 'string') {
      return error;
    }

    if ((error as { error?: { message?: string } })?.error?.message) {
      return String((error as { error: { message: string } }).error.message);
    }

    if ((error as { message?: string })?.message) {
      return String((error as { message: string }).message);
    }

    return 'Request failed';
  }

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNewOrderSelect),
      mergeMap((action) => {
        const key = action.key;
        if (this.pending[key]) {
          return EMPTY;
        }
        this.pending[key] = true;

        const idbCatalog = this.getIdbCatalog(action.option, action.params);
        if (idbCatalog) {
          return from(this.idb.selectOtCatalog(idbCatalog)).pipe(
            switchMap((idbData) => {
              if (idbData && (Array.isArray(idbData) ? idbData.length : true)) {
                delete this.pending[key];
                return of(loadNewOrderSelectSuccess({ key, data: idbData as unknown[] }));
              }
              return this.fetchAndPersist(action, key, idbCatalog);
            }),
            catchError(() => this.fetchAndPersist(action, key, idbCatalog)),
          );
        }

        return this.api.getSelect(action.option, action.params, action.tenantCode).pipe(
          map((data) => loadNewOrderSelectSuccess({ key, data })),
          catchError((error) => of(loadNewOrderSelectFailure({ key, error: this._toErrorMessage(error) }))),
          finalize(() => {
            delete this.pending[key];
          }),
        );
      }),
    ),
  );

  private getIdbCatalog(option: string, params: string): IdbOtCatalogName | null {
    if (params && params !== '') return null;
    return IDB_CATALOG_BY_OPTION[option] || null;
  }

  private fetchAndPersist(
    action: { option: string; params: string; tenantCode?: string },
    key: string,
    idbCatalog: IdbOtCatalogName,
  ) {
    return this.api.getSelect(action.option, action.params, action.tenantCode).pipe(
      tap((data) => {
        this.idb.saveOtCatalog(idbCatalog, data as IdbOtCatalogTypeMap[typeof idbCatalog]).catch(() => undefined);
      }),
      map((data) => loadNewOrderSelectSuccess({ key, data })),
      catchError((error) => of(loadNewOrderSelectFailure({ key, error: this._toErrorMessage(error) }))),
      finalize(() => {
        delete this.pending[key];
      }),
    );
  }
}
